import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';

interface ModuleSection {
  title: string;
  content: string[];
}


const moduleContent: ModuleSection[] = [
  {
    title: 'Nutrition for Long-Term Wellness',
    content: [
      'Long-term wellness is supported by consistent healthy eating patterns.',
      'Healthy eating habits can help prevent chronic diseases and support overall well-being.',
      // removed source
    ]
  },
  {
    title: 'Strategies for Lifelong Nutrition',
    content: [
      '• Focus on whole foods and limit processed foods.',
      '• Stay hydrated and plan meals ahead.',
      '• Read nutrition labels and make gradual changes for sustainable results.',
      // removed source
    ]
  },
  {
    title: 'Balancing Diet and Activity',
    content: [
      '• Balance your diet with regular physical activity.',
      '• Adjust food choices based on age, health, and lifestyle.',
      '• Monitor progress and celebrate healthy changes.',
      // removed source
    ]
  },
  {
    title: 'Maintaining Healthy Habits',
    content: [
      '• Make gradual changes for sustainable results.',
      '• Seek support from family and community.',
      '• Stay informed about nutrition and wellness.',
      '• Practice self-care and stress management.',
      // removed source
    ]
  }
];


const HealthyEatingModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('healthyEatingM4');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;


  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, nextStep === totalSteps - 1);
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/wellness-loop/healthy-eating-habits');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const handleFinish = async () => {
    await saveProgress(currentStep, true);
    navigate('/panel/wellness-loop/healthy-eating-habits');
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  const showCompletionMessage = safeCurrentStep === totalSteps - 1;

  return (
    <div className="module-form">
      <div className="module-container">
        {/* Left Panel - Illustration */}
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Healthy Eating Module 4</h1>
            <p className="module-subtitle">Nutrition for Long-Term Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn strategies for lifelong nutrition and how healthy eating habits support long-term wellness.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={safeCurrentStep === 0 ? handleFinish : handleBack}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {safeCurrentStep < totalSteps - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleFinish}
              >
                Finish Module
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthyEatingModule4Page;

